import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Home, Settings as SettingsIcon, X, ChevronUp, ChevronDown, Maximize, Minimize } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fontStyleToCss } from "@/lib/kioskGrid";
import { KioskCanvas } from "@/components/KioskCanvas";
import { KioskFormDialog } from "@/components/KioskFormDialog";
import { InstallPrompt } from "@/components/InstallPrompt";
import { toast } from "sonner";


function KioskControls({ style, position, size, onHome, onSettings }) {
  const [open, setOpen] = useState(style !== "slide_out");
  useEffect(() => { setOpen(style !== "slide_out"); }, [style]);
  const pos = {
    "bottom-right": "bottom-6 right-6",
    "bottom-left": "bottom-6 left-6",
    "top-right": "top-6 right-6",
    "top-left": "top-6 left-6",
  }[position] || "bottom-6 right-6";
  const isBottom = position?.startsWith("bottom");
  const isRight = position?.endsWith("right");
  const sizes = { sm: { btn: "w-10 h-10", icon: "w-4 h-4" }, md: { btn: "w-12 h-12", icon: "w-5 h-5" }, lg: { btn: "w-14 h-14", icon: "w-6 h-6" } };
  const s = sizes[size] || sizes.sm;

  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);
  const toggleFullscreen = () => {
    const el = document.documentElement;
    const inFs = document.fullscreenElement || document.webkitFullscreenElement;
    if (!inFs) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      req && req.call(el).catch(() => {});
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      exit && exit.call(document).catch(() => {});
    }
  };
  const FsIcon = isFs ? Minimize : Maximize;
  const fsBtn = (cls) => (
    <button onClick={toggleFullscreen} className={cls} aria-label={isFs ? "Exit full screen" : "Full screen"} data-testid="kiosk-fullscreen-button"><FsIcon className={`${s.icon} text-[#2A2626]`} /></button>
  );

  if (style === "pill") {
    return (
      <div className={`fixed ${pos} z-[60] flex items-center gap-1 bg-white/90 backdrop-blur-md shadow-lg border border-[#E5E2DC] rounded-full p-1`}>
        <button onClick={onHome} className={`${s.btn} kiosk-tap rounded-full flex items-center justify-center hover:bg-[#F0EBE1]`} aria-label="Home" data-testid="kiosk-home-button"><Home className={`${s.icon} text-[#2A2626]`} /></button>
        <div className="w-px h-5 bg-[#E5E2DC]" />
        <button onClick={onSettings} className={`${s.btn} kiosk-tap rounded-full flex items-center justify-center hover:bg-[#F0EBE1]`} aria-label="Settings" data-testid="kiosk-settings-gear"><SettingsIcon className={`${s.icon} text-[#2A2626]`} /></button>
        <div className="w-px h-5 bg-[#E5E2DC]" />
        {fsBtn(`${s.btn} kiosk-tap rounded-full flex items-center justify-center hover:bg-[#F0EBE1]`)}
      </div>
    );
  }
  if (style === "stacked") {
    return (
      <div className={`fixed ${pos} z-[60] flex flex-col gap-2`}>
        <button onClick={onHome} className={`${s.btn} kiosk-tap rounded-full bg-white shadow-lg border border-[#E5E2DC] flex items-center justify-center`} aria-label="Home" data-testid="kiosk-home-button"><Home className={`${s.icon} text-[#2A2626]`} /></button>
        <button onClick={onSettings} className={`${s.btn} kiosk-tap rounded-full bg-white shadow-lg border border-[#E5E2DC] flex items-center justify-center`} aria-label="Settings" data-testid="kiosk-settings-gear"><SettingsIcon className={`${s.icon} text-[#2A2626]`} /></button>
        {fsBtn(`${s.btn} kiosk-tap rounded-full bg-white shadow-lg border border-[#E5E2DC] flex items-center justify-center`)}
      </div>
    );
  }
  // slide_out (default) — subtle tab/handle that expands
  const Chev = isBottom ? ChevronUp : ChevronDown;
  return (
    <div className={`fixed ${pos} z-[60]`}>
      <div className={`flex items-center gap-1 transition-all duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} style={{ transform: open ? "translateY(0)" : `translateY(${isBottom ? "10px" : "-10px"})` }}>
        <button onClick={onHome} className={`${s.btn} kiosk-tap rounded-full bg-white/95 backdrop-blur shadow-md border border-[#E5E2DC] flex items-center justify-center`} aria-label="Home" data-testid="kiosk-home-button"><Home className={`${s.icon} text-[#2A2626]`} /></button>
        <button onClick={onSettings} className={`${s.btn} kiosk-tap rounded-full bg-white/95 backdrop-blur shadow-md border border-[#E5E2DC] flex items-center justify-center`} aria-label="Settings" data-testid="kiosk-settings-gear"><SettingsIcon className={`${s.icon} text-[#2A2626]`} /></button>
        {fsBtn(`${s.btn} kiosk-tap rounded-full bg-white/95 backdrop-blur shadow-md border border-[#E5E2DC] flex items-center justify-center`)}
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`${open ? "opacity-40 hover:opacity-100" : "opacity-30 hover:opacity-80"} transition-opacity absolute ${isBottom ? "-top-7" : "-bottom-7"} ${isRight ? "right-1" : "left-1"} w-7 h-5 rounded-md bg-[#2A2626]/70 backdrop-blur flex items-center justify-center`}
        aria-label="Toggle controls"
        data-testid="kiosk-controls-toggle"
      >
        <Chev className="w-3 h-3 text-white" />
      </button>
    </div>
  );
}

export default function Kiosk() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [now, setNow] = useState(new Date());
  const [view, setView] = useState({ name: "home" }); // home | content | passcode
  const [passcode, setPasscode] = useState("");
  const [carouselIdx, setCarouselIdx] = useState(0);
  const timerRef = useRef(null);
  const viewedRef = useRef(false);

  const track = useCallback((type, extra = {}) => {
    api.post("/public/track", { slug, type, ...extra }).catch(() => {});
  }, [slug]);

  const cfg = tenant?.config;

  useEffect(() => {
    if (cfg && !viewedRef.current) { viewedRef.current = true; track("kiosk_view"); }
  }, [cfg, track]);
  const eff = cfg;
  const activeScreen = null;

  const loadTenant = useCallback(async () => {
    try {
      // A registered Grow device pairs once via ?device=<token>, then remembers it.
      let deviceToken = null;
      try {
        const p = new URLSearchParams(window.location.search).get("device");
        if (p) { localStorage.setItem(`kiosk_device_${slug}`, p); deviceToken = p; }
        else { deviceToken = localStorage.getItem(`kiosk_device_${slug}`); }
      } catch { /* ignore */ }
      const q = deviceToken ? `?device_token=${encodeURIComponent(deviceToken)}` : "";
      const { data } = await api.get(`/kiosk/${slug}${q}`);
      setTenant(data);
      try { localStorage.setItem("kiosk_slug", slug); } catch { /* ignore */ }
    } catch {
      toast.error("Kiosk not found");
    }
  }, [slug]);

  useEffect(() => { loadTenant(); }, [loadTenant]);

  // Lightweight revalidation: pick up manual Connect Experience changes (and, with Grow,
  // scheduled Experience transitions) without relaunching the kiosk. No WebSockets.
  useEffect(() => {
    const i = setInterval(() => { loadTenant(); }, 30000);
    return () => clearInterval(i);
  }, [loadTenant]);

  // Point the installed PWA at THIS church's kiosk (launches straight into it on Android/Chrome).
  useEffect(() => {
    if (!tenant) return;
    const name = tenant?.config?.brand_name || tenant?.name || "MinistryHub Kiosk";
    const manifest = {
      name: `${name} — Welcome Kiosk`,
      short_name: name.slice(0, 24),
      description: "Church welcome kiosk for first-time guests and connections.",
      start_url: window.location.pathname,
      scope: "/",
      display: "standalone",
      orientation: "any",
      background_color: tenant?.config?.bg_color || "#FAF9F6",
      theme_color: tenant?.config?.bg_color || "#FAF9F6",
      icons: [
        { src: "/icon-192.png", sizes: "192x192", type: "image/png", purpose: "any" },
        { src: "/icon-512.png", sizes: "512x512", type: "image/png", purpose: "any" },
        { src: "/icon-maskable-512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
      ],
    };
    let url;
    let link;
    try {
      const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
      url = URL.createObjectURL(blob);
      link = document.querySelector('link[rel="manifest"]');
      if (link) link.setAttribute("href", url);
    } catch { /* keep static manifest */ }
    return () => {
      if (url) URL.revokeObjectURL(url);
      if (link) link.setAttribute("href", "/manifest.json");
    };
  }, [tenant]);

  // Clock
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // Inactivity reset
  const resetIdleTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const secs = cfg?.timeout_seconds || 60;
    timerRef.current = setTimeout(() => {
      console.log("[kiosk-diag] RETURN-HOME reason=inactivity_timeout secs=", secs);
      setView({ name: "home" });
      setPasscode("");
    }, secs * 1000);
  }, [cfg?.timeout_seconds]);

  useEffect(() => {
    if (!cfg) return;
    const events = ["touchstart", "mousedown", "keydown", "scroll"];
    const handler = () => resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [cfg, resetIdleTimer]);

  // Carousel auto-advance (pro)
  useEffect(() => {
    const visibleCount = (cfg?.featured_events || []).filter((e) => e && e.visible !== false).length;
    if (!cfg?.carousel_mode || visibleCount <= 1) return;
    const i = setInterval(() => {
      setCarouselIdx((p) => (p + 1) % visibleCount);
    }, 6000);
    return () => clearInterval(i);
  }, [cfg]);

  // TEMP DIAGNOSTICS — kiosk view/route + external-iframe lifecycle (console only, no behavior change).
  useEffect(() => {
    console.log("[kiosk-diag] view ->", view.name, view.iframe ? `(iframe ${view.iframe})` : "");
  }, [view]);
  useEffect(() => {
    if (view.name === "content" && view.iframe) {
      console.log("[kiosk-diag] external iframe MOUNTED src=", view.iframe);
      return () => console.log("[kiosk-diag] external iframe UNMOUNTED src=", view.iframe);
    }
  }, [view.name, view.iframe]);

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-12 h-12 border-4 border-[#2A2626] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const openAction = (a, title) => {
    const lt = a.link_type
      || (a.form_id ? "form" : a.link_value ? "url" : (a.app_store_url || a.play_store_url) ? "app_download" : "page");
    if (lt === "form" && a.form_id) {
      track("form_open", { form_id: a.form_id });
      api.get(`/public/form/${a.form_id}`)
        .then(({ data }) => setView({ name: "form", form: data }))
        .catch(() => setView({ name: "content", title, markdown: "This form is unavailable right now." }));
    } else if (lt === "app_download") {
      setView({ name: "app", title, appStoreUrl: a.app_store_url || "", playStoreUrl: a.play_store_url || "" });
    } else if (lt === "url" && a.link_value) {
      setView({ name: "content", title, iframe: a.link_value });
    } else {
      setView({ name: "content", title, markdown: a.page_content || "" });
    }
  };

  const openButton = (btn) => {
    track("button_tap", { button_id: btn.id });
    if ((btn.link_type === "form" || btn.form_id) && btn.form_id) track("qr_open", { button_id: btn.id });
    openAction(btn, btn.label);
  };

  const openEvent = (ev) => {
    track("spotlight_tap", { event_id: ev.id });
    openAction(ev, ev.title);
  };

  const verifyPasscode = async () => {
    try {
      await api.post("/kiosk/passcode/verify", { slug, passcode });
      toast.success("PIN accepted");
      // Signal intentional admin access so the PWA doesn't bounce back to the kiosk.
      try { sessionStorage.setItem("admin_override", "1"); } catch { /* ignore */ }
      // Send user to login (admin must be authenticated to edit)
      navigate("/login");
    } catch {
      toast.error("Incorrect PIN");
      setPasscode("");
    }
  };

  return (
    <div
      className="min-h-screen kiosk-mode grain"
      style={{ backgroundColor: cfg.bg_color, ...fontStyleToCss(cfg.default_font) }}
    >
      <KioskCanvas
        cfg={cfg}
        eff={eff}
        activeScreen={activeScreen}
        now={now}
        carouselIdx={carouselIdx}
        onButton={openButton}
        onEvent={openEvent}
        onQr={(p) => { track("qr_open", { button_id: p.button_id }); setView({ name: "qr", title: p.title, url: p.url }); }}
      />

      {/* Persistent kiosk controls — style/position configurable */}
      <KioskControls
        style={cfg.controls_style || "slide_out"}
        position={cfg.controls_position || "bottom-left"}
        size={cfg.controls_size || "sm"}
        onHome={() => setView({ name: "home" })}
        onSettings={() => setView({ name: "passcode" })}
      />

      {/* Content overlay */}
      {view.name === "content" && (
        <div className="fixed inset-0 z-30 bg-[#FAF9F6] flex flex-col fade-up">
          <div className="px-8 py-5 border-b border-[#E5E2DC] flex items-center justify-between bg-white">
            <h2 className="text-2xl font-semibold" style={fontStyleToCss(cfg.heading_font)}>{view.title}</h2>
            <button onClick={() => setView({ name: "home" })} className="w-12 h-12 rounded-full bg-[#F0EBE1] flex items-center justify-center kiosk-tap" data-testid="kiosk-content-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {view.iframe ? (
              <iframe src={view.iframe} title={view.title} className="w-full h-full min-h-[70vh] border-0" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation" onLoad={() => console.log("[kiosk-diag] iframe onLoad (NOT treated as submission complete) src=", view.iframe)} />
            ) : (
              <div className="max-w-3xl mx-auto px-8 py-12 whitespace-pre-wrap text-lg leading-relaxed" style={fontStyleToCss(cfg.default_font)}>
                {view.markdown || "No content yet."}
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR code modal */}
      <Dialog open={view.name === "qr"} onOpenChange={(o) => !o && setView({ name: "home" })}>
        <DialogContent className="max-w-sm" data-testid="kiosk-qr-dialog">
          <DialogHeader>
            <DialogTitle>{view.title || "Scan to open"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white p-4 rounded-2xl border border-[#E5E2DC]">
              <QRCodeSVG value={view.url || ""} size={220} level="M" marginSize={2} data-testid="kiosk-qr-svg" />
            </div>
            <p className="text-xs text-center text-[#5C5856] break-all px-2" data-testid="kiosk-qr-url">{view.url}</p>
          </div>
          <Button onClick={() => setView({ name: "home" })} className="w-full h-11 bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="kiosk-qr-close">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Download-our-app modal (App Store / Play Store QR codes) */}
      <Dialog open={view.name === "app"} onOpenChange={(o) => !o && setView({ name: "home" })}>
        <DialogContent className="max-w-lg" data-testid="kiosk-app-dialog">
          <DialogHeader>
            <DialogTitle>{view.title || "Download our app"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5856]">Scan with your phone camera to download.</p>
          <div className="flex flex-wrap justify-center gap-8 py-4">
            {view.appStoreUrl && (
              <div className="flex flex-col items-center gap-3" data-testid="kiosk-app-ios">
                <div className="bg-white p-3 rounded-2xl border border-[#E5E2DC]">
                  <QRCodeSVG value={view.appStoreUrl} size={170} level="M" marginSize={2} />
                </div>
                <span className="text-sm font-semibold text-[#2A2626]">App Store (iOS)</span>
              </div>
            )}
            {view.playStoreUrl && (
              <div className="flex flex-col items-center gap-3" data-testid="kiosk-app-android">
                <div className="bg-white p-3 rounded-2xl border border-[#E5E2DC]">
                  <QRCodeSVG value={view.playStoreUrl} size={170} level="M" marginSize={2} />
                </div>
                <span className="text-sm font-semibold text-[#2A2626]">Google Play (Android)</span>
              </div>
            )}
            {!view.appStoreUrl && !view.playStoreUrl && (
              <p className="text-sm text-[#5C5856] py-6">No app links configured yet.</p>
            )}
          </div>
          <Button onClick={() => setView({ name: "home" })} className="w-full h-11 bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="kiosk-app-close">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Custom form modal */}
      {view.name === "form" && view.form && (
        <KioskFormDialog form={view.form} accentColor={cfg.accent_color} onClose={() => setView({ name: "home" })} />
      )}

      {/* Passcode modal */}
      <Dialog open={view.name === "passcode"} onOpenChange={(o) => !o && setView({ name: "home" })}>
        <DialogContent className="max-w-sm" data-testid="kiosk-passcode-dialog">
          <DialogHeader>
            <DialogTitle>Staff PIN</DialogTitle>
          </DialogHeader>
          <Input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Enter staff PIN"
            className="h-12 text-center text-2xl tracking-[0.5em]"
            data-testid="kiosk-passcode-input"
            autoFocus
          />
          <Button onClick={verifyPasscode} className="w-full h-11 bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="kiosk-passcode-submit">
            Continue
          </Button>
          <Link to="/" className="text-xs text-center text-[#5C5856] hover:text-[#2A2626]">Exit kiosk</Link>
        </DialogContent>
      </Dialog>
      <InstallPrompt />
    </div>
  );
}

// (orphan-cleanup-marker)

